/**
 * Logs de balanza Bluetooth — store en memoria para la WEB (2026-10-15).
 *
 * Espejo del patrón de la app mobile (`mobile/src/services/balanzaLogService.ts`,
 * commit fb3d2ad) adaptado a browser: sin persistencia (un reload corta la
 * conexión serial igual) y con exportación por portapapeles / descarga .txt
 * para que el usuario mande el registro por WhatsApp.
 *
 * Store EXTERNO al DeviceContext a propósito: el polling {RW} escribe un TX
 * por segundo y poner `logs` en el value del contexto re-renderizaría todo el
 * árbol de consumidores. Con useSyncExternalStore sólo se re-renderiza quien
 * se suscribe (el modal de diagnóstico).
 */
import { useSyncExternalStore } from 'react';

export type NivelLogBalanza = 'INFO' | 'TX' | 'RX' | 'WARN' | 'ERROR';

export interface EntradaLogBalanza {
  /** 'HH:MM:SS' local. */
  ts: string;
  nivel: NivelLogBalanza;
  mensaje: string;
}

/** Tope en memoria (mismo criterio que la mobile). */
const MAX_ENTRADAS = 300;

let entradas: EntradaLogBalanza[] = [];
const listeners = new Set<() => void>();
/** Contador de versión: useSyncExternalStore compara por identidad, así que
 *  cada push genera un array nuevo (no mutamos el compartido). */
function notificar(): void {
  listeners.forEach((cb) => cb());
}

/** Agrega una entrada de log (console.log siempre — útil en chrome://inspect). */
export function addLogBalanza(nivel: NivelLogBalanza, mensaje: string): void {
  const ts = new Date().toLocaleTimeString('es-AR', { hour12: false });
  console.log(`[Balanza] [${nivel}] ${mensaje}`);
  entradas = [...entradas.slice(-(MAX_ENTRADAS - 1)), { ts, nivel, mensaje }];
  notificar();
}

/** Snapshot de las entradas (máx 300, la más nueva al final). */
export function getLogsBalanza(): EntradaLogBalanza[] {
  return entradas;
}

export function subscribeLogsBalanza(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function limpiarLogsBalanza(): void {
  entradas = [];
  notificar();
}

/** Hook reactivo sobre el store (sólo re-renderiza el suscriptor). */
export function useBalanzaLogs(): EntradaLogBalanza[] {
  return useSyncExternalStore(subscribeLogsBalanza, getLogsBalanza);
}

/** Texto plano con header de contexto (para copiar / descargar / WhatsApp). */
export function textoLogsBalanza(): string {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const ua = nav?.userAgent ?? 'UA no disponible';
  const plataforma = (nav as any)?.userAgentData?.platform ?? (nav?.userAgent ?? '');
  const header = [
    '═══════════════════════════════════════════',
    `DIAGNÓSTICO BALANZA WEB · ${new Date().toLocaleString('es-AR')}`,
    `Plataforma: ${plataforma || 'n/d'}`,
    `User-Agent: ${ua}`,
    `Web Serial: ${nav && 'serial' in nav ? 'disponible' : 'NO disponible'} · Contexto seguro: ${
      typeof window !== 'undefined' && window.isSecureContext !== undefined ? window.isSecureContext : 'n/d'
    }`,
    '═══════════════════════════════════════════',
  ].join('\n');
  const lineas = entradas.map((e) => `[${e.ts}] [${e.nivel}] ${e.mensaje}`);
  return `${header}\n${lineas.join('\n')}`;
}
